import { useEffect, useState } from 'react';
import { useViewMode } from '@/shared/hooks/useViewMode';
import { Chip } from '@/shared/ui/atoms';
import { PageSlice, Pagination } from '@/shared/ui/Pagination';
import { ViewModeToggle, type ViewModeOption } from '@/shared/ui/ViewModeToggle';
import { SortPicker } from '@/features/project-board/components/SortPicker';
import { DateSort, type SortKey } from '@/features/project-board/sorting';
import { BoardSearch, useBoard } from '@/features/project-board/BoardProvider';
import { ItemKey } from '@/features/project-board/identity';
import { OwnerRoster } from '@/features/project-board/owners';
import type { DrawerPayload } from '@/features/project-board/components/DetailDrawer';
import { DrawerPayloads } from '@/features/project-board/drawer-payloads';
import type { Epic, StageState, Task } from '@/features/project-board/types';
import { ViewFrame } from '@/features/project-board/views/ViewFrame';

/** 단계별 색 — 초반(문서 작성)은 회색, 구현은 주황, 검증 이후는 파랑/초록으로 단계감을 준다. */
const STAGE_TONE: Record<string, string> = {
  prd: 'border-line bg-line-soft text-muted',
  design: 'border-line bg-line-soft text-muted',
  'spec-review': 'border-line bg-line-soft text-muted',
  tasks: 'border-line bg-line-soft text-muted',
  execution: 'border-amber-200 bg-amber-50 text-amber-800',
  review: 'border-accent-line bg-accent-soft text-[#0a3069]',
  qa: 'border-accent-line bg-accent-soft text-[#0a3069]',
  sync: 'border-emerald-200 bg-emerald-50 text-done',
};

const RAIL_TONE: Record<string, string> = {
  filled: 'bg-done/70',
  partial: 'bg-progress/70',
  absent: 'bg-line-soft',
};

type EpicViewMode = 'card' | 'table';

const PAGE_SIZE = 20;

const SORT_OPTIONS = DateSort.options('등록일');

const VIEW_MODES: ReadonlyArray<ViewModeOption<EpicViewMode>> = [
  { key: 'card', label: '카드', glyph: '▤' },
  { key: 'table', label: '테이블', glyph: '☰' },
];

export function EpicView(): React.JSX.Element {
  const { index, epics: scoped, filters } = useBoard();
  const [mode, setMode] = useViewMode<EpicViewMode>('project-board.epic.view', 'table');
  // 기본 정렬은 최근 등록일 순 — 등록일 기준 필드는 스펙 폴더 경로의 year/month다(sorting.ts).
  const [sort, setSort] = useViewMode<SortKey>('project-board.epic.sort', 'date-desc');
  // 연결 task는 필터와 무관하게 전부 보여준다 — 에픽을 열었으면 그 에픽의 전모를 봐야 한다.
  const allTasks = index?.tasks ?? [];

  // 등록 시점은 스펙 폴더 경로의 연·월이라 월 단위다. 같은 달은 slug으로 순서를 고정한다.
  const epics = DateSort.sort(
    scoped.filter(
      (epic) =>
        BoardSearch.matches(searchTextOf(epic, allTasks), filters.query),
    ),
    sort,
    (epic) => `${epic.year}-${epic.month}`,
    (epic) => epic.slug,
  );

  // 필터·검색·정렬이 바뀌면 결과 길이가 달라지므로 첫 페이지로 돌린다.
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [filters, sort]);
  const paged = PageSlice.of(epics, page, PAGE_SIZE);

  return (
    <ViewFrame
      scope="epics"
      countLabel={`${epics.length}개 에픽`}
      isEmpty={epics.length === 0}
      emptyMessage="조건에 맞는 에픽이 없다."
      toolbar={
        <div className="flex items-center gap-2">
          <SortPicker options={SORT_OPTIONS} value={sort} onChange={setSort} label="에픽 정렬" />
          <ViewModeToggle options={VIEW_MODES} value={mode} onChange={setMode} />
        </div>
      }
      render={(open) => (
        <>
          {mode === 'table' ? (
            <EpicTable epics={paged.items} tasks={allTasks} open={open} />
          ) : (
          <div className="flex flex-col gap-7">
            {groupByPeriod(paged.items).map((group) => (
              <section key={group.period}>
                <h2 className="mb-3 flex items-center gap-2.5 text-[13px] font-bold">
                  {group.period}
                  <span className="rounded-full bg-line-soft px-1.5 text-[11px] font-semibold text-muted">
                    {group.epics.length}
                  </span>
                  <span className="h-px flex-1 bg-line" />
                </h2>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(420px,1fr))] gap-3.5">
                  {group.epics.map((epic) => (
                    <EpicCard key={ItemKey.epic(epic)} epic={epic} tasks={allTasks} open={open} />
                  ))}
                </div>
              </section>
            ))}
          </div>
          )}
          <Pagination page={paged.page} totalPages={paged.totalPages} onChange={setPage} />
        </>
      )}
    />
  );
}

function EpicCard({
  epic,
  tasks,
  open,
}: {
  epic: Epic;
  tasks: Task[];
  open: (payload: DrawerPayload) => void;
}): React.JSX.Element {
  const passed = epic.stages.filter((stage) => stage.fill === 'filled').length;

  return (
    <article
      onClick={() => open(DrawerPayloads.epic(epic, tasks))}
      className="flex cursor-pointer flex-col gap-3 rounded-xl border border-line bg-surface px-5 py-4 transition hover:border-accent hover:shadow-sm"
    >
      <h3 className="line-clamp-2 text-[17px] leading-snug font-bold tracking-tight">{displayTitle(epic)}</h3>

      <div className="flex items-center gap-2">
        <span className={`rounded-full border px-2 py-0.5 text-[11.5px] font-semibold ${STAGE_TONE[epic.stage] ?? STAGE_TONE.prd}`}>
          {epic.stageLabel}
        </span>
        <span className="text-xs text-muted">
          단계 {passed}/{epic.stages.length}
        </span>
        <span className="flex-1" />
        <span className="text-xs text-faint">{epic.updatedAt ?? '갱신 기록 없음'}</span>
      </div>

      <p className={`line-clamp-2 text-[13px] leading-relaxed ${epic.summary ? 'text-[#424a53]' : 'text-faint'}`}>
        {epic.summary || '요약 없음'}
      </p>

      {/*
        개발요청에서 시작한 스펙만 표시한다 — 대부분은 없고, 붙이면 "이건 다른 팀 요청이었다"가 카드에서
        바로 읽힌다. 번호는 PRD의 `Dev request` 절에서 색인이 읽은 값이다.
      */}
      {epic.devRequestId !== null && (
        <p className="text-[11.5px] text-muted">
          출처: 개발요청 <span className="font-mono">#{epic.devRequestId}</span>
        </p>
      )}

      {/* 단계 레일 — 라벨은 툴팁으로만. 카드마다 8개 라벨을 깔면 그것만 눈에 들어온다. */}
      <div className="grid grid-flow-col auto-cols-fr gap-[3px]" aria-label={`단계 ${passed}/${epic.stages.length} 통과`}>
        {epic.stages.map((stage) => (
          <span
            key={stage.key}
            title={`${stage.label}: ${stageWord(stage)}`}
            className={`h-1 rounded-full ${RAIL_TONE[stage.fill]} ${stage.key === epic.stage ? 'ring-2 ring-accent/25' : ''}`}
          />
        ))}
      </div>

      <div className="mt-auto flex flex-col gap-2 border-t border-line-soft pt-3">
        <div className="flex items-center gap-2.5">
          <strong className="min-w-[42px] text-[17px] tabular-nums">{epic.donePct}%</strong>
          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-line-soft">
            <i className="block h-full rounded-full bg-done" style={{ width: `${epic.donePct}%` }} />
          </span>
        </div>

        <div className="flex items-center gap-3.5 text-[11.5px] text-muted">
          <span>
            완료 <strong className="text-ink tabular-nums">{epic.counts.done}</strong>
          </span>
          <span>
            진행 <strong className="text-ink tabular-nums">{epic.counts.progress}</strong>
          </span>
          <span>
            대기 <strong className="text-ink tabular-nums">{epic.counts.todo}</strong>
          </span>
          {epic.counts.dropped > 0 && <span className="text-faint">제외 {epic.counts.dropped}</span>}
          <span className="flex-1" />
          <Participants tasks={tasks} epic={epic} />
        </div>

        {epic.skippedStages.length > 0 && (
          <div className="text-[11.5px] text-amber-800" title={`연속 통과: ${epic.gateStageLabel}`}>
            ⚠ 건너뜀 — {epic.skippedStages.join(', ')}
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * 테이블 뷰 — 카드가 훑는 화면이라면 이건 **비교하는 화면**이다. 요약문을 빼고 진행률·카운트·단계를
 * 같은 열에 세워 에픽끼리 눈으로 비교되게 한다.
 */
function EpicTable({
  epics,
  tasks,
  open,
}: {
  epics: Epic[];
  tasks: Task[];
  open: (payload: DrawerPayload) => void;
}): React.JSX.Element {
  return (
    // 이 래퍼에 overflow를 주면 thead sticky 기준이 래퍼로 바뀌어 헤더가 첫 행 뒤로 밀린다 — 주지 않는다.
    <div className="rounded-lg border border-line bg-surface">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col />
          <col className="w-[92px]" />
          <col className="w-[150px]" />
          <col className="w-[124px]" />
          <col className="w-[78px]" />
          <col className="w-[170px]" />
          <col className="w-[150px]" />
          <col className="w-[104px]" />
          <col className="w-[96px]" />
        </colgroup>
        <thead>
          <tr className="[&>th]:sticky [&>th]:top-topbar [&>th]:z-10 [&>th]:bg-[#f0f3f6] [&>th]:px-2.5 [&>th]:py-2.5 [&>th]:text-left [&>th]:align-middle [&>th]:text-xs [&>th]:font-semibold [&>th]:text-muted [&>th]:shadow-[inset_0_-1px_0_var(--color-line)]">
            <th>에픽</th>
            <th>기간</th>
            <th>현재 단계</th>
            <th>다음 단계</th>
            <th className="!text-right">진행률</th>
            <th>진행</th>
            <th>담당</th>
            <th>task</th>
            <th>갱신</th>
          </tr>
        </thead>
        <tbody>
          {epics.map((epic) => (
            <tr
              key={ItemKey.epic(epic)}
              onClick={() => open(DrawerPayloads.epic(epic, tasks))}
              className="cursor-pointer border-b border-line-soft last:border-0 hover:bg-canvas [&>td]:px-2.5 [&>td]:py-2.5 [&>td]:align-middle"
            >
              <td>
                <div className="text-[13.5px] font-semibold">{displayTitle(epic)}</div>
                <div className="mt-0.5 truncate font-mono text-[11px] text-faint">{epic.slug}</div>
              </td>
              <td className="text-xs whitespace-nowrap text-muted">
                {epic.year}-{epic.month}
              </td>
              <td>
                <span
                  className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STAGE_TONE[epic.stage] ?? STAGE_TONE.prd}`}
                >
                  {epic.stageLabel}
                </span>
                <div className="mt-0.5 text-[11px] text-muted">
                  {epic.stages.filter((stage) => stage.fill === 'filled').length}/{epic.stages.length} 통과
                  {epic.skippedStages.length > 0 && (
                    <span className="text-amber-800" title={epic.skippedStages.join(', ')}>
                      {' '}
                      ⚠ {epic.skippedStages.length}
                    </span>
                  )}
                </div>
              </td>
              <td>
                {/*
                  다음 관문은 아직 열지 않은 것이라 현재 단계와 같은 무게로 칠하지 않는다 — 점선 테두리로
                  "예정"임을 말한다. 마지막 단계까지 채운 에픽은 가리킬 곳이 없어 빈 칸이다.
                */}
                {epic.nextStageLabel === null ? (
                  <span className="text-[11px] text-faint">-</span>
                ) : (
                  <span className="inline-block rounded-full border border-dashed border-line px-2 py-0.5 text-[11px] font-semibold text-muted">
                    {epic.nextStageLabel}
                  </span>
                )}
              </td>
              <td className="text-right text-sm font-bold tabular-nums">{epic.donePct}%</td>
              <td>
                <span className="block h-1.5 overflow-hidden rounded-full bg-line-soft">
                  <i className="block h-full rounded-full bg-done" style={{ width: `${epic.donePct}%` }} />
                </span>
                <div className="mt-1 text-[11px] text-muted">
                  완료 {epic.counts.done}/{epic.activeCount}
                </div>
              </td>
              <td>
                <Participants tasks={tasks} epic={epic} />
              </td>
              <td className="text-[11.5px] text-muted">
                <span className="mr-2.5">
                  진행 <strong className="text-ink">{epic.counts.progress}</strong>
                </span>
                <span>
                  대기 <strong className="text-ink">{epic.counts.todo}</strong>
                </span>
                {epic.counts.dropped > 0 && <span className="ml-2.5 text-faint">제외 {epic.counts.dropped}</span>}
              </td>
              <td className="text-[11px] whitespace-nowrap text-muted">{epic.updatedAt ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * 에픽 참여자 — 에픽 문서에는 담당자 필드가 없어서 **그 에픽 task의 담당자**를 건수 순으로 보여준다.
 * 셋까지 노출하고 나머지는 `+N`으로 접는다(폭이 좁고, 상위 몇 명이면 누가 주도하는지 충분히 보인다).
 */
function Participants({ tasks, epic }: { tasks: Task[]; epic: Epic }): React.JSX.Element {
  const people = OwnerRoster.epicPeopleOf(epic, tasks);
  if (people.length === 0) return <span className="text-[11px] text-faint">-</span>;

  const shown = people.slice(0, 3);
  const rest = people.length - shown.length;

  return (
    <span className="flex items-center gap-1">
      {shown.map((person) => (
        <span
          key={person.name}
          title={person.lead ? `${person.name} · 담당자(기획 문서 작성) · task ${person.count}건` : `${person.name} · task ${person.count}건`}
          className={`grid size-[22px] shrink-0 place-items-center rounded-full text-[10px] font-bold ${
            person.lead ? 'bg-accent-soft text-accent ring-1 ring-accent-line' : 'bg-line-soft text-muted'
          }`}
        >
          {person.name.slice(0, 2).toUpperCase()}
        </span>
      ))}
      {rest > 0 && <span className="text-[11px] text-faint">+{rest}</span>}
    </span>
  );
}

/**
 * 검색 대상 — 문서 텍스트에 담당자를 더한다. 검색창이 "담당자 검색"이라고 안내하는데 에픽만 이름으로
 * 안 걸리면 필터가 고장 난 것으로 보인다. 명부 이름(`jeff`)과 문서에 적힌 원문(`김용휘`)을 둘 다 넣어
 * 어느 쪽으로 쳐도 걸리게 한다.
 */
function searchTextOf(epic: Epic, tasks: Task[]): string {
  const people = OwnerRoster.epicPeopleOf(epic, tasks).map((person) => person.name);
  return [epic.slug, epic.title, epic.summary, epic.owner, ...people].join(' ');
}

/** 기간(연-월)으로 묶는다 — 55개를 한 그리드에 쏟으면 무엇이 최근 일인지 안 보인다. */
function groupByPeriod(epics: Epic[]): Array<{ period: string; epics: Epic[] }> {
  const groups: Array<{ period: string; epics: Epic[] }> = [];
  for (const epic of epics) {
    const period = `${epic.year}-${epic.month}`;
    const group = groups.find((candidate) => candidate.period === period);
    if (group) group.epics.push(epic);
    else groups.push({ period, epics: [epic] });
  }
  return groups;
}

/**
 * PRD에 사람이 읽을 제목이 없으면 API가 slug를 제목으로 준다. 그때만 하이픈을 풀어 읽기 쉽게 한다
 * (실제 제목은 그대로 — 원문을 고쳐 보여주지 않는다).
 */
function displayTitle(epic: Epic): string {
  return epic.title === epic.slug ? epic.slug.replace(/-/g, ' ') : epic.title;
}

function stageWord(stage: StageState): string {
  if (stage.fill === 'filled') return '작성됨';
  return stage.fill === 'partial' ? '일부 작성' : '없음';
}

export { Chip };
